import { useState } from 'react';

import { Center, Flex, Spinner } from '@chakra-ui/react';

import { PaginationState, SortingState } from '@tanstack/react-table';

import { useQueryClient } from '@tanstack/react-query';

import { format, isValid, parseISO } from 'date-fns';

import {
  buildError,
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  PayCycle,
  PayCycleStatus,
  useToast,
} from '@aslanpay/portal-common-lib';

import { Table } from 'app/components/UI/Table/Table';

import { defaultPageSize } from 'app/constants/table';

import useFetchPayCycles from 'app/hooks/useFetchPayCyclesPage';
import useUpdatePayCycle from 'app/hooks/useUpdatePayCycle';

import getPayCyclesColumns from 'app/utils/table/payCycles';
import { validateDate } from 'app/utils/validations';
import { useSearchParams } from 'react-router-dom';

import { PayCalendarPayCyclesTabProps } from './types';

import type { Filter } from '@aslanpay/portal-common-lib';

const STAGE_FILTER = 'Stage';
const DEFAULT_STAGES: PayCycleStatus[] = ['IN_PROGRESS', 'PENDING'];
const STAGE_OPTIONS: DropdownOption[] = [
  { value: 'IN_PROGRESS', text: 'Current' },
  { value: 'PENDING', text: 'Upcoming' },
  { value: 'COMPLETED', text: 'Past' },
];

/**
 * @description Names a stage filter value for its chip.
 */
const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
  (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
  String(filterValue);

function PayCalendarPayCyclesTab({
  payCalendarId,
  payCalendarLocation,
  payFrequency,
  isReadOnly = false,
  setIsTableInEditMode,
}: PayCalendarPayCyclesTabProps) {
  const queryClient = useQueryClient();

  const toast = useToast();

  const [searchParams] = useSearchParams();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [stageFilter, setStageFilter] = useState<PayCycleStatus[]>(DEFAULT_STAGES);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const { data: payCycles, isInitialLoading: isLoadingPayCycles } = useFetchPayCycles(
    {
      payCalendarId: payCalendarId || '',
      status: stageFilter,
      sortBy: sorting[0]?.id ?? 'startDate',
      sort: !sorting[0] || !sorting[0]?.desc ? 'ASC' : 'DESC',
      page: pageIndex,
      size: pageSize,
    },
    payCalendarLocation || 'ENGLAND_WALES'
  );

  const { mutate: updatePayCycle, isLoading: isUpdatingPayCycle } = useUpdatePayCycle({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Pay cycles updated',
        description: 'The changes were successfully saved!',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchPayCycles', payCalendarId] });
    },
    onError: () => {},
  });

  const handleIsValidPayCycle = (payCycle: PayCycle) => {
    const originalPayCycle = payCycles?.list.find((pc) => pc.id === payCycle.id);

    const parsedCutoffDate = parseISO(payCycle.cutoffDate);

    const strippedOriginalCutoffDate = originalPayCycle
      ? format(parseISO(originalPayCycle.cutoffDate), 'yyyy-MM-dd')
      : '';
    const strippedCutoffDate = isValid(parsedCutoffDate)
      ? format(parsedCutoffDate, 'yyyy-MM-dd')
      : payCycle.cutoffDate;

    const hasChanges =
      strippedCutoffDate !== strippedOriginalCutoffDate ||
      payCycle.payday !== originalPayCycle?.payday;

    return hasChanges && validateDate(payCycle.cutoffDate) && validateDate(payCycle.payday);
  };

  const handleConfirmUpdatePayCycle = (updatedData: PayCycle[], onCloseEditMode: () => void) => {
    const changedRows = updatedData.filter((row) => handleIsValidPayCycle(row));

    if (changedRows.length === 0) return;

    // Para cada linha alterada, chama a mutação
    changedRows.forEach((changedRow) => {
      updatePayCycle(
        {
          id: changedRow.id,
          payCalendarId: payCalendarId || '',
          cutoffDate: changedRow.cutoffDate,
          payday: changedRow.payday,
          odpAllowancePercentage: changedRow.odpAllowancePercentage,
        },
        {
          onSuccess: () => {
            // Só fecha o modo de edição quando todas as alterações forem feitas
            // Pode ajustar lógica se quiser esperar todas completarem antes de fechar
            onCloseEditMode();
          },
          onError: (error) => {
            const baseError = `Sorry, it was not possible to save the pay cycle. ${buildError(
              error,
              ''
            )}`;
            toast({
              position: 'top-right',
              status: 'error',
              title: 'Something went wrong',
              description: baseError,
            });
          },
        }
      );
    });
  };

  const filters = [
    { name: STAGE_FILTER, type: 'category', data: STAGE_OPTIONS, selectedValues: stageFilter },
  ] as Filter[];

  const handleFilterChange = (_: string, values: (string | number)[]) => {
    setStageFilter(values as PayCycleStatus[]);
    setPagination({ pageIndex: 0, pageSize });
  };

  return (
    <Flex flexDir="column" gap="20px" w="full">
      <Filters
        activeFilter={activeFilter}
        displayFormatter={formatFilterValue}
        filters={filters}
        onFilterClose={() => setActiveFilter(undefined)}
        onFilterChange={handleFilterChange}
        onFilterClick={setActiveFilter}
      />
      {isLoadingPayCycles || !payCycles ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getPayCyclesColumns(payFrequency, isReadOnly)}
          data={payCycles.list || []}
          pageNumber={payCycles.pageNumber || pageIndex}
          pageSize={payCycles.pageSize || pageSize}
          total={payCycles.total || 0}
          sorting={sorting}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onIsValidRow={handleIsValidPayCycle}
          onSetEditingRow={(isEditing) => setIsTableInEditMode(isEditing)}
          onConfirmUpdateRow={handleConfirmUpdatePayCycle}
          isUpdatingRow={isUpdatingPayCycle}
          showHover={false}
        />
      )}
    </Flex>

    /*
          <Table
            columns={getCompanyColumns(actions)}
            data={companies?.list || []}
            total={companies?.total || 0}
            pageNumber={companies?.pageNumber || pageIndex}
            pageSize={companies?.pageSize || pageSize}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
          />

*/
  );
}

export default PayCalendarPayCyclesTab;
